import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { addUpsell, getUpsellEligibility, type UpsellEligibility } from '../../lib/upsell';
import { formatCurrency } from '../../lib/format';

interface Props {
  orderId: string;
  onClose: () => void;
  onAdded: () => void;
}

export function UpsellRandReparatieDialog({ orderId, onClose, onAdded }: Props) {
  const [eligibility, setEligibility] = useState<UpsellEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUpsellEligibility(orderId, 'rand-reparatie', 0)
      .then((data) => {
        if (cancelled) return;
        setEligibility(data);
        setSelectedIds(new Set(data.items.map((i) => i.id)));
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const items = eligibility?.items ?? [];
  const hasItems = items.length > 0;

  const total = useMemo(() => {
    let sum = 0;
    for (const item of items) {
      if (!selectedIds.has(item.id)) continue;
      sum += applyDiscount ? item.priceInclVat * 0.7 : item.priceInclVat;
    }
    return sum;
  }, [items, selectedIds, applyDiscount]);

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await addUpsell({
        workOrderId: orderId,
        type: 'rand-reparatie',
        itemIds: Array.from(selectedIds),
        discount: applyDiscount ? 30 : 0,
      });
      if (!result.success) {
        setError(result.message || 'Rand reparatie toevoegen mislukt');
        setSubmitting(false);
        return;
      }
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rand reparatie toevoegen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Rand reparatie toevoegen</h2>
          {eligibility?.workOrderNumber && (
            <p className="text-xs text-slate-500 mt-1">
              {eligibility.workOrderNumber}
              {eligibility.customerName ? ` — ${eligibility.customerName}` : ''}
            </p>
          )}
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {loading && <p className="text-sm text-slate-500">Laden…</p>}
          {!loading && eligibility?.alreadyOrdered && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Rand reparatie is al besteld voor deze order.
            </p>
          )}
          {!loading && !hasItems && !eligibility?.alreadyOrdered && (
            <p className="text-sm text-slate-500">
              Geen kleden met losse randen gedetecteerd bij voorinspectie. Neem contact op
              met operatie als dit alsnog nodig is.
            </p>
          )}
          {!loading && hasItems && !eligibility?.alreadyOrdered && (
            <div className="space-y-2">
              {items.map((item) => {
                const selected = selectedIds.has(item.id);
                const price = applyDiscount ? item.priceInclVat * 0.7 : item.priceInclVat;
                return (
                  <label
                    key={item.id}
                    className={clsx(
                      'flex items-center gap-3 px-3 py-2 border rounded cursor-pointer',
                      selected ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleItem(item.id)}
                      className="h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{item.name}</div>
                      {item.m2 != null && <div className="text-xs text-slate-500">{item.m2.toFixed(2)} m²</div>}
                    </div>
                    <div className="text-sm text-slate-900">{formatCurrency(price)}</div>
                  </label>
                );
              })}

              <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Totaal</span>
                  <span className="font-medium text-slate-900">{formatCurrency(total)}</span>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={applyDiscount}
                    onChange={(e) => setApplyDiscount(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  30% coulance-korting toepassen (alleen bij klacht of campagne)
                </label>
              </div>
            </div>
          )}
          {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loading || selectedIds.size === 0 || !hasItems || !!eligibility?.alreadyOrdered}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || loading || selectedIds.size === 0 || !hasItems || !!eligibility?.alreadyOrdered
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Toevoegen…' : 'Bevestigen'}
          </button>
        </div>
      </div>
    </div>
  );
}
